import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews } from "@/utils/content";

interface HeroSectionProps {
  featured: Article;
  picks: Article[];
}

export default function HeroSection({ featured, picks }: HeroSectionProps) {
  return (
    <section className="max-w-portal mx-auto px-4 md:px-6 pt-8 md:pt-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* 焦点大图 */}
        <article className="lg:col-span-2 group">
          <Link
            to={`/article/${featured.slug}`}
            className="block relative w-full h-[300px] md:h-[440px] overflow-hidden rounded-lg bg-foreground-900 cursor-pointer"
          >
            <img
              src={featured.cover}
              alt={`${featured.title} - 拍摄现场幕后焦点报道配图`}
              title={`${featured.title} 现场直击`}
              className="w-full h-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/35 to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-accent-500 text-background-50 text-xs font-bold">
                <i className="ri-flashlight-fill w-3 h-3 flex items-center justify-center" />
                焦点直击
              </span>
              <span className="px-2.5 py-1 rounded bg-background-50/90 text-foreground-900 text-xs font-bold">
                {featured.categoryName}
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
              <h2 className="font-heading text-2xl md:text-4xl font-black text-background-50 leading-tight max-w-3xl">
                {featured.title}
              </h2>
              <p className="hidden sm:block text-sm md:text-base text-background-200 leading-relaxed mt-3 max-w-2xl line-clamp-2">
                {featured.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs md:text-sm text-background-300">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                  {featured.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                  {featured.date}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                  {formatViews(featured.views)} 阅读
                </span>
              </div>
            </div>
          </Link>
        </article>

        {/* 编辑推荐 */}
        <div className="flex flex-col rounded-lg border border-background-200 bg-background-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200">
            <h3 className="font-heading text-base font-bold text-foreground-950">编辑推荐</h3>
            <span className="text-xs text-foreground-500">今日精选</span>
          </div>
          <div className="flex flex-col px-5 py-2 flex-1">
            {picks.map((item, idx) => (
              <Link
                key={item.id}
                to={`/article/${item.slug}`}
                className="group flex items-start gap-3 py-3.5 border-b border-background-200 last:border-0 cursor-pointer"
              >
                <span className="w-6 h-6 shrink-0 flex items-center justify-center font-heading font-black text-sm text-primary-700">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <h4 className="text-sm font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-accent-700 transition-colors">
                    {item.title}
                  </h4>
                  <span className="inline-block text-xs text-secondary-700 mt-1">
                    {item.categoryName}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}